/**
 * Single analytics interface for the whole site.
 *
 * Call sites only ever use `analytics.track(...)` — the concrete provider is
 * wired up inside this module, so switching providers means editing this file
 * (and the mounting component in components/shared/DeferredVercelAnalytics.tsx),
 * never the call sites.
 *
 * Events fired before the provider is ready (it mounts at browser idle) are
 * buffered and flushed on init instead of being dropped. On the next stack,
 * named events wait for analytics consent. Statistics only covers the automatic
 * pageview and Speed Insights mount.
 */

import { allowsAnalytics, getConsentGate } from 'lib/consent/gate';
import { classifyRoute } from 'lib/consent/policy';
import { enqueueEmit, registerVercelSink } from 'lib/analytics/fanout';
import type { CanonicalEvent } from 'lib/analytics/catalog';

export type AnalyticsEventProps = Record<
  string,
  string | number | boolean | null | undefined
>;

export interface AnalyticsProvider {
  track: (event: string, props?: AnalyticsEventProps) => void;
}

const MAX_BUFFERED_EVENTS = 50;

let provider: AnalyticsProvider | null = null;
let buffer: Array<[string, AnalyticsEventProps | undefined]> = [];

function trackVercel(event: string, props?: AnalyticsEventProps): void {
  if (typeof window === 'undefined') return;
  if (!allowsAnalytics()) return;
  if (provider) {
    provider.track(event, props);
    return;
  }
  if (buffer.length < MAX_BUFFERED_EVENTS) {
    buffer.push([event, props]);
  }
}

registerVercelSink((event, props) => {
  trackVercel(event, props as AnalyticsEventProps | undefined);
});

function track(
  event: CanonicalEvent | (string & {}),
  props?: AnalyticsEventProps,
  legacy?: string | readonly string[],
): void {
  if (typeof window === 'undefined') return;
  if (getConsentGate().stack === 'next') {
    enqueueEmit({ name: event, props, legacy });
    return;
  }
  trackVercel(event, props);
}

function setProvider(next: AnalyticsProvider): void {
  provider = next;
  const pending = buffer;
  buffer = [];
  if (!allowsAnalytics()) return;
  for (const [event, props] of pending) {
    next.track(event, props);
  }
}

/**
 * Current provider: Vercel Web Analytics. Loaded dynamically so the SDK never
 * enters the shared first-load chunk; the <Analytics/> component (mounted by
 * DeferredVercelAnalytics) must already be in the tree so events are accepted.
 */
export async function initAnalyticsProvider(): Promise<void> {
  if (typeof window === 'undefined' || provider) return;
  try {
    const mod = await import('@vercel/analytics');
    setProvider({ track: mod.track });
  } catch {
    // Analytics must never break the app; events simply stop buffering.
    buffer = [];
  }
}

/**
 * Vercel stats keep the path and utm params only (no click ids, emails or
 * tokens), and nothing from private pages. The script also follows in-app
 * navigation, so this runs on every beacon.
 */
export function statsUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (classifyRoute(parsed.pathname) === 'private') return null;
    for (const key of [...parsed.searchParams.keys()]) {
      if (!key.startsWith('utm_')) parsed.searchParams.delete(key);
    }
    return parsed.toString();
  } catch {
    return null;
  }
}

export const analytics = { track };
